// Schema-driven attribute form (Module 01 §2.3, §3.1). Renders and validates a
// product's `attributes` from its attribute schema's `json_schema` + `ui_hints`.
// Used by S1 (create) and S2 (Attributes tab).
//
// Validation mirrors the documented JSON-Schema draft 2020-12 SUBSET the API
// enforces (attribute-validation.ts): object-level `required` + `properties` +
// `additionalProperties:false`; per-property `type` (incl. array unions),
// `enum`, `pattern`. Anything else in the schema is ignored; a malformed/empty
// schema constrains nothing. Client validation is a fast-feedback mirror only —
// the server remains the source of truth, and its field-level errors (message
// keys, D-022) are surfaced VERBATIM here, never assumed to be present.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../../components/select";
import type { FieldError } from "../../../../lib/request";
import { parseUiHints } from "./ui-hints";

export interface AttributeError {
  path: string;
  message_key: string;
  params?: Record<string, unknown> | undefined;
}

// ---- schema parsing ---------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

const SCALAR_TYPES = ["string", "number", "integer", "boolean", "object", "array", "null"] as const;
type ScalarType = (typeof SCALAR_TYPES)[number];

interface PropertySchema {
  types: ScalarType[];
  enumValues: unknown[] | undefined;
  pattern: string | undefined;
}

function parseProperty(raw: unknown): PropertySchema {
  const obj = asRecord(raw) ?? {};
  const typeRaw = obj["type"];
  const typeList = Array.isArray(typeRaw) ? typeRaw : typeRaw !== undefined ? [typeRaw] : [];
  const types = typeList.filter((t): t is ScalarType => SCALAR_TYPES.includes(t as ScalarType));
  const enumRaw = obj["enum"];
  const patternRaw = obj["pattern"];
  return {
    types,
    enumValues: Array.isArray(enumRaw) ? enumRaw : undefined,
    pattern: typeof patternRaw === "string" ? patternRaw : undefined,
  };
}

interface ParsedSchema {
  properties: Record<string, PropertySchema>;
  order: string[];
  required: Set<string>;
  additionalPropertiesFalse: boolean;
}

function parseSchema(jsonSchema: Record<string, unknown> | undefined): ParsedSchema {
  const schema = asRecord(jsonSchema);
  const propsRaw = asRecord(schema?.["properties"]) ?? {};
  const properties: Record<string, PropertySchema> = {};
  const order: string[] = [];
  for (const [key, raw] of Object.entries(propsRaw)) {
    properties[key] = parseProperty(raw);
    order.push(key);
  }
  return {
    properties,
    order,
    required: new Set(asStringArray(schema?.["required"])),
    additionalPropertiesFalse: schema?.["additionalProperties"] === false,
  };
}

function matchesType(value: unknown, types: ScalarType[]): boolean {
  if (types.length === 0) return true; // untyped property constrains nothing
  return types.some((type) => {
    switch (type) {
      case "string":
        return typeof value === "string";
      case "number":
        return typeof value === "number" && Number.isFinite(value);
      case "integer":
        return typeof value === "number" && Number.isInteger(value);
      case "boolean":
        return typeof value === "boolean";
      case "object":
        return asRecord(value) !== undefined;
      case "array":
        return Array.isArray(value);
      case "null":
        return value === null;
      default:
        return false;
    }
  });
}

function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Validate `values` against the `json_schema` subset. Pure + exported so a
 * caller (S1 create) can block submit. Returns one {@link AttributeError} per
 * violation with an i18n message key under `catalogue.attr.errors.*`.
 */
export function validateAttributes(
  jsonSchema: Record<string, unknown> | undefined,
  values: Record<string, unknown>,
): AttributeError[] {
  const schema = parseSchema(jsonSchema);
  const errors: AttributeError[] = [];

  for (const key of schema.required) {
    if (isBlank(values[key])) {
      errors.push({ path: key, message_key: "catalogue.attr.errors.required" });
    }
  }

  for (const [key, value] of Object.entries(values)) {
    const prop = schema.properties[key];
    if (prop === undefined) {
      if (schema.additionalPropertiesFalse) {
        errors.push({ path: key, message_key: "catalogue.attr.errors.unknownKey" });
      }
      continue;
    }
    if (isBlank(value)) continue; // required handled above; blank optional is fine

    if (!matchesType(value, prop.types)) {
      const key_ = prop.types.includes("integer")
        ? "catalogue.attr.errors.integer"
        : prop.types.includes("number")
          ? "catalogue.attr.errors.number"
          : "catalogue.attr.errors.type";
      errors.push({ path: key, message_key: key_ });
      continue;
    }
    if (prop.enumValues !== undefined && !prop.enumValues.includes(value)) {
      errors.push({ path: key, message_key: "catalogue.attr.errors.enum" });
    }
    if (prop.pattern !== undefined && typeof value === "string") {
      let re: RegExp | undefined;
      try {
        re = new RegExp(prop.pattern);
      } catch {
        re = undefined; // invalid stored pattern constrains nothing
      }
      if (re !== undefined && !re.test(value)) {
        errors.push({ path: key, message_key: "catalogue.attr.errors.pattern" });
      }
    }
  }

  return errors;
}

// ---- form -------------------------------------------------------------------

function matchServerErrors(serverErrors: readonly FieldError[], key: string): FieldError[] {
  return serverErrors.filter(
    (e) => e.path === key || e.path.endsWith(`/${key}`) || e.path.endsWith(`.${key}`),
  );
}

export interface SchemaAttributeFormProps {
  /** The product's attribute schema (its `json_schema` + `ui_hints`). */
  jsonSchema?: Record<string, unknown> | undefined;
  uiHints?: Record<string, unknown> | undefined;
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  /** Field-level errors returned by a rejected save — surfaced verbatim. */
  serverErrors?: readonly FieldError[] | undefined;
  disabled?: boolean | undefined;
}

export function SchemaAttributeForm({
  jsonSchema,
  uiHints,
  value,
  onChange,
  serverErrors = [],
  disabled = false,
}: SchemaAttributeFormProps) {
  const { t, i18n } = useTranslation();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const schema = parseSchema(jsonSchema);
  const hints = parseUiHints(uiHints, i18n.language);
  const clientErrors = validateAttributes(jsonSchema, value);

  // Render order: ui_hints.order first (for the keys it lists), then any
  // remaining schema properties in declaration order.
  const ordered = [
    ...hints.order.filter((k) => k in schema.properties),
    ...schema.order.filter((k) => !hints.order.includes(k)),
  ];

  if (ordered.length === 0) {
    // No schema / no properties → nothing to edit. Unknown-key server errors (if
    // any) still surface below.
    if (serverErrors.length === 0) return null;
  }

  function setField(key: string, next: unknown) {
    const updated: Record<string, unknown> = { ...value };
    if (next === undefined) {
      updated[key] = undefined;
    } else {
      updated[key] = next;
    }
    onChange(updated);
    setTouched((prev) => ({ ...prev, [key]: true }));
  }

  // Server errors whose path does not map to a rendered field (object-level).
  const unmatchedServer = serverErrors.filter(
    (e) =>
      !ordered.some((k) => e.path === k || e.path.endsWith(`/${k}`) || e.path.endsWith(`.${k}`)),
  );

  return (
    <div className="schema-form">
      {ordered.map((key) => {
        const prop = schema.properties[key];
        if (prop === undefined) return null;
        const label = hints.labels[key] ?? key;
        const unit = hints.units[key];
        const required = schema.required.has(key);
        const fieldServerErrors = matchServerErrors(serverErrors, key);
        const clientError =
          touched[key] === true ? clientErrors.find((e) => e.path === key) : undefined;
        const fieldId = `attr-${key}`;
        return (
          <div className="schema-field" key={key} data-field={key}>
            <label htmlFor={fieldId}>
              {/* Already locale-resolved by `parseUiHints` from the pack's own
                  `fields[key].labels` map — it is human text, not an i18n key. */}
              {label}
              {required ? null : (
                <span className="field-optional"> ({t("catalogue.attr.optional")})</span>
              )}
              {unit !== undefined ? <span className="field-unit"> ({unit})</span> : null}
            </label>
            <AttributeInput
              id={fieldId}
              prop={prop}
              value={value[key]}
              disabled={disabled}
              onChange={(next) => {
                setField(key, next);
              }}
              selectPlaceholder={t("catalogue.attr.selectPlaceholder")}
              trueLabel={t("catalogue.attr.booleanTrue")}
              falseLabel={t("catalogue.attr.booleanFalse")}
              unsupportedLabel={t("catalogue.attr.unsupportedField")}
            />
            {clientError !== undefined ? (
              <p className="field-error" role="alert">
                {t(clientError.message_key, { defaultValue: clientError.message_key })}
              </p>
            ) : null}
            {fieldServerErrors.map((e, i) => (
              <p className="field-error field-error-server" role="alert" key={i}>
                {t(e.message_key, { ...(e.params ?? {}), defaultValue: e.message_key })}
              </p>
            ))}
          </div>
        );
      })}

      {unmatchedServer.length > 0 ? (
        <div className="schema-form-errors" role="alert">
          {unmatchedServer.map((e, i) => (
            <p className="field-error field-error-server" key={i}>
              {e.path}: {t(e.message_key, { ...(e.params ?? {}), defaultValue: e.message_key })}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AttributeInput({
  id,
  prop,
  value,
  disabled,
  onChange,
  selectPlaceholder,
  trueLabel,
  falseLabel,
  unsupportedLabel,
}: {
  id: string;
  prop: PropertySchema;
  value: unknown;
  disabled: boolean;
  onChange: (next: unknown) => void;
  selectPlaceholder: string;
  trueLabel: string;
  falseLabel: string;
  unsupportedLabel: string;
}) {
  // enum → select
  if (prop.enumValues !== undefined) {
    const current =
      typeof value === "string" || typeof value === "number" || typeof value === "boolean"
        ? String(value)
        : "";
    return (
      <Select
        id={id}
        disabled={disabled}
        value={current}
        onChange={(e) => {
          onChange(e.target.value === "" ? undefined : e.target.value);
        }}
      >
        <option value="">{selectPlaceholder}</option>
        {prop.enumValues.map((opt) => {
          const optStr = String(opt);
          return (
            <option value={optStr} key={optStr}>
              {optStr}
            </option>
          );
        })}
      </Select>
    );
  }

  const primaryType = prop.types[0];

  if (primaryType === "boolean") {
    return (
      <Select
        id={id}
        disabled={disabled}
        value={value === true ? "true" : value === false ? "false" : ""}
        onChange={(e) => {
          onChange(e.target.value === "" ? undefined : e.target.value === "true");
        }}
      >
        <option value="">{selectPlaceholder}</option>
        <option value="true">{trueLabel}</option>
        <option value="false">{falseLabel}</option>
      </Select>
    );
  }

  if (primaryType === "number" || primaryType === "integer") {
    return (
      <input
        id={id}
        className="ds-input"
        type="number"
        step={primaryType === "integer" ? 1 : "any"}
        disabled={disabled}
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(undefined);
            return;
          }
          const parsed = Number(raw);
          onChange(Number.isNaN(parsed) ? raw : parsed);
        }}
      />
    );
  }

  if (primaryType === "object" || primaryType === "array" || primaryType === "null") {
    return <p className="field-unsupported">{unsupportedLabel}</p>;
  }

  // default: string
  return (
    <input
      id={id}
      className="ds-input"
      type="text"
      disabled={disabled}
      value={
        typeof value === "string"
          ? value
          : typeof value === "number" || typeof value === "boolean"
            ? String(value)
            : ""
      }
      onChange={(e) => {
        onChange(e.target.value === "" ? undefined : e.target.value);
      }}
    />
  );
}
